import { useEffect, useRef, useState } from 'react';
import { useLenis } from '../components/SmoothScroll';

const PEEK_HEIGHT = 50;
// How quickly the monitor catches up to the scroll target (0–1 per frame).
// Lower = more floaty/laggy glide, higher = snappier.
const SMOOTHING = 0.09;

// Ease the scroll progress so motion accelerates/decelerates instead of being linear.
const easeInOutCubic = (t) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

export function useHeroMonitorScroll() {
  const heroSectionRef = useRef(null);
  const monitorRef = useRef(null);
  const [offsetY, setOffsetY] = useState(null);
  const lenis = useLenis();

  // Mutable animation state kept out of React renders.
  const targetRef = useRef(null);
  const currentRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const computeTarget = () => {
      const section = heroSectionRef.current;
      const monitor = monitorRef.current;
      if (!section || !monitor) return;

      const sectionTop = section.offsetTop;
      const sectionHeight = section.offsetHeight;
      const viewportH = window.innerHeight;
      const scrollY = window.scrollY;

      const scrollRange = Math.max(sectionHeight - viewportH, 1);
      const rawProgress = Math.min(1, Math.max(0, (scrollY - sectionTop) / scrollRange));
      const progress = easeInOutCubic(rawProgress);

      const monitorHeight = monitor.offsetHeight;
      const hiddenAmount = Math.max(monitorHeight - PEEK_HEIGHT, 0);
      targetRef.current = hiddenAmount * (1 - progress);
    };

    // Continuous loop: glide the current value toward the scroll target.
    const tick = () => {
      const target = targetRef.current;
      if (target !== null) {
        if (currentRef.current === null) {
          currentRef.current = target;
        } else {
          currentRef.current += (target - currentRef.current) * SMOOTHING;
        }

        // Snap when close enough to avoid endless sub-pixel updates.
        if (Math.abs(target - currentRef.current) < 0.1) {
          currentRef.current = target;
        }

        setOffsetY(currentRef.current);
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    const onScroll = () => computeTarget();

    computeTarget();
    rafRef.current = requestAnimationFrame(tick);

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    if (lenis) lenis.on('scroll', onScroll);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (lenis) lenis.off('scroll', onScroll);
    };
  }, [lenis]);

  return { heroSectionRef, monitorRef, offsetY, peekHeight: PEEK_HEIGHT };
}
